import { useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { supabase, type Recipe } from '../lib/supabase'
import { isPlusRequiredError } from '../lib/billing'
import {
  importedImage, importedIngredients, isVideoSource, recipeFromImport, type ImportedIngredient, type ImportedRecipe, type NewRecipe,
} from '../lib/recipeImport'
import { itemKey } from '../lib/normalize'
import { fillMissing, type RefreshAdded } from '../lib/recipeRefresh'
import type { RecipeFields } from '../lib/recipeDraft'
import { PHOTO_BUCKET, photoPath, resizePhoto, IMPORTED_PHOTO_EDGE } from '../lib/photo'
import { revertRow, snapshotRow } from '../lib/rows'
import { useIsVisible } from './useIsVisible'

export const RECIPES_KEY = (householdId: string) => ['recipes', householdId]


/**
 * Why an import didn't save, as the `recipe` function says (by code):
 * - no_recipe: a page with nothing to read; text_no_recipe: pasted text without ingredients;
 * - needs_caption: a post whose recipe has to be pasted: Instagram's caption behind its login
 *   wall, a TikTok caption cut short, or a recipe the post says is in its comments or bio (`where`);
 * - fetch_failed: the site didn't answer or wouldn't let us read it; rate_limited: too many at once;
 * - failed: anything else (offline, a server error).
 */
export type ImportError =
  | 'no_recipe' | 'text_no_recipe' | 'needs_caption' | 'subscription_required' | 'source_blocked'
  | 'fetch_failed' | 'rate_limited' | 'failed'
/** Where a post says its recipe is (needs_caption). */
export type RecipeElsewhere = 'comments' | 'bio'
export type ImportFailure = { ok: false; error: ImportError; where?: RecipeElsewhere }
/** The codes the function answers that the app tells apart; anything else is 'failed'. */
const ANSWERED: readonly string[] = ['no_recipe', 'needs_caption', 'subscription_required', 'source_blocked', 'fetch_failed', 'rate_limited']
/** An ingredient the import named the way the household already does. */
export interface ImportRename { index: number; from: string; to: string }
export type ImportOutcome =
  | {
    ok: true; id: string; title: string; renamed: ImportRename[]
    /** Every ingredient as saved, with the line it came from (lib/importReview.ts); empty for a
     *  video that didn't list them (the editor opens). */
    ingredients: ImportedIngredient[]
    /** A page read only in part (no ingredient list on it): its text, for the editor to show beside the recipe. */
    sourceText?: string
  }
  | ImportFailure
export type RefreshOutcome = { ok: true; filled: RefreshAdded[] } | ImportFailure

/** What a member edits on a saved recipe; starred and tags are the household's. Ingredients: the import review's names. */
export type RecipePatch = Partial<Pick<Recipe, 'starred' | 'tags' | 'vegetarian' | 'ingredients'>>

const newestFirst = (a: Recipe, b: Recipe) => b.created_at.localeCompare(a.created_at)

/** What the editor does to a recipe's photo: leave it, take it away, or a new (resized) one. */
export type PhotoChange = { kind: 'keep' } | { kind: 'remove' } | { kind: 'set'; blob: Blob }
export type SaveError = 'subscription_required' | 'photo_failed' | 'failed'
export type SaveOutcome = { ok: true; id: string } | { ok: false; error: SaveError }

// Best effort: an object nothing points at any more. A failure leaves an
// orphan in the household's folder, never a broken recipe.
function removePhotos(paths: (string | null | undefined)[]) {
  const gone = paths.filter((p): p is string => !!p)
  if (gone.length) void supabase.storage.from(PHOTO_BUCKET).remove(gone).catch(() => {})
}

/**
 * The household's recipes, newest first. Importing from a link saves the
 * recipe (a second import of the same link refreshes it — usually that is a
 * correction, or the steps an older import didn't read). Star, tags and the
 * vegetarian override are optimistic and roll back on error; another phone's
 * changes arrive over Realtime (033).
 */
export function useRecipes(householdId: string, memberId: string, options: { onPlusRequired?: () => void } = {}) {
  const qc = useQueryClient()
  const isVisible = useIsVisible()
  const key = RECIPES_KEY(householdId)
  const { onPlusRequired } = options

  const recipesQuery = useQuery({
    queryKey: key,
    enabled: !!householdId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recipes').select('*')
        .eq('household_id', householdId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as Recipe[]
    },
  })

  useEffect(() => {
    if (!isVisible || !householdId) return
    const channel = supabase
      .channel(`recipes:${householdId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'recipes', filter: `household_id=eq.${householdId}` },
        (payload) => {
          qc.setQueryData<Recipe[]>(RECIPES_KEY(householdId), (old = []) => {
            if (payload.eventType === 'DELETE') return old.filter(r => r.id !== (payload.old as Recipe).id)
            const row = payload.new as Recipe
            const rest = old.filter(r => r.id !== row.id)
            return [...rest, row].sort(newestFirst)
          })
        })
      // Joined, or joined again after the tab was hidden: read what realtime missed.
      .subscribe(status => {
        if (status === 'SUBSCRIBED') void qc.invalidateQueries({ queryKey: RECIPES_KEY(householdId) })
      })
    return () => { supabase.removeChannel(channel) }
  }, [householdId, qc, isVisible])

  // The `recipe` function's reading of a page, a post or pasted text, as a
  // recipe's fields and its image. A video may come back without ingredients —
  // saved anyway, for the member to fill in — and so may text with only a
  // method, and a page read only in part (with its text to copy from); a page
  // with nothing on it is no recipe.
  async function fetchImport(asked: { url: string; caption?: string } | { text: string }): Promise<
    { ok: true; fields: NewRecipe; lines: ImportedIngredient[]; image: Blob | null; sourceText?: string } | ImportFailure
  > {
    const url = 'url' in asked ? asked.url : null
    const body = 'text' in asked ? { text: asked.text } : asked.caption ? { url: asked.url, caption: asked.caption } : { url: asked.url }
    let data: ImportedRecipe | null
    try {
      const res = await supabase.functions.invoke<ImportedRecipe>('recipe', { body })
      if (res.error) {
        const ctx = (res.error as { context?: Response }).context
        let answer: { error?: unknown; where?: unknown } | undefined
        try {
          if (ctx && typeof ctx.json === 'function') answer = await ctx.json()
        } catch { /* not JSON */ }
        const code = ctx?.status === 429 ? 'rate_limited' : String(answer?.error ?? '')
        const error: ImportError = !ANSWERED.includes(code) ? 'failed' : code === 'no_recipe' && url == null ? 'text_no_recipe' : code as ImportError
        const where = answer?.where === 'comments' || answer?.where === 'bio' ? answer.where : undefined
        return { ok: false, error, ...(where ? { where } : {}) }
      }
      data = res.data
    } catch {
      return { ok: false, error: 'failed' }
    }
    const nothing = { ok: false, error: url == null ? 'text_no_recipe' : 'no_recipe' } as const
    if (!data || !Array.isArray(data.ingredients)) return nothing
    const onlyMethod = url == null && (data.steps?.length ?? 0) > 0
    const sourceText = data.partial && typeof data.pageText === 'string' && data.pageText.trim() ? data.pageText : undefined
    if (data.ingredients.length === 0 && !isVideoSource(data.source) && !onlyMethod && !sourceText) return nothing
    return {
      ok: true, fields: recipeFromImport(data, url), lines: importedIngredients(data), image: importedImage(data.image),
      ...(sourceText ? { sourceText } : {}),
    }
  }

  /**
   * The import's image, resized on the device like a member's photo and kept
   * in the household's private folder. Never over a member's own photo.
   * Returns the new path, or null when there is none to keep.
   */
  async function keepImportedPhoto(recipeId: string, image: Blob | null, current?: Recipe): Promise<string | null> {
    if (!image || (current?.photo_path && !current.photo_imported)) return null
    const resized = await resizePhoto(image, IMPORTED_PHOTO_EDGE)
    if (!resized.ok) return null
    const path = photoPath(householdId, recipeId)
    const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, resized.blob, { contentType: 'image/jpeg', upsert: false })
    return error ? null : path
  }

  /**
   * Imports a link, or refreshes the recipe already saved from it. `nameFor`
   * names each ingredient the way the household already does (lib/catalog.ts
   * spellingFor); what it changed comes back, for the import review to show
   * and undo. `caption` is an Instagram post's, pasted by the member. Importing
   * a link again keeps the aisle a member picked for an ingredient it still has.
   */
  async function importRecipe(url: string, nameFor: (name: string) => string = n => n, caption?: string): Promise<ImportOutcome> {
    const fetched = await fetchImport({ url, caption })
    if (!fetched.ok) return fetched
    return saveImport(fetched, nameFor, (qc.getQueryData<Recipe[]>(key) ?? []).find(r => r.url === url))
  }

  /**
   * A recipe's text the member pasted (copied out of a screenshot with Live
   * Text, a message, a book): read like a caption by the `recipe` function and
   * always saved as a new recipe — there is no link to know it by.
   */
  async function importText(text: string, nameFor: (name: string) => string = n => n): Promise<ImportOutcome> {
    const fetched = await fetchImport({ text })
    if (!fetched.ok) return fetched
    return saveImport(fetched, nameFor)
  }

  /** Saves what an import read: a new recipe, or over `existing` (the same link imported before). */
  async function saveImport(
    fetched: { fields: NewRecipe; lines: ImportedIngredient[]; image: Blob | null; sourceText?: string },
    nameFor: (name: string) => string,
    existing?: Recipe,
  ): Promise<ImportOutcome> {
    const picked = new Map((existing?.ingredients ?? []).flatMap(g => (g.category_id ? [[itemKey(g.name), g.category_id] as const] : [])))
    const renamed: ImportRename[] = []
    const ingredients: ImportedIngredient[] = fetched.lines.map((line, index) => {
      const to = nameFor(line.name)
      if (to !== line.name) renamed.push({ index, from: line.name, to })
      const category = picked.get(itemKey(to)) ?? picked.get(itemKey(line.name))
      return { ...line, name: to, ...(category ? { category_id: category } : {}) }
    })
    const fields = { ...fetched.fields, ingredients: ingredients.map(({ raw: _raw, issues: _issues, ...g }) => g) }
    const source = fetched.sourceText ? { sourceText: fetched.sourceText } : {}
    const id = existing?.id ?? uuid()
    const photo = await keepImportedPhoto(id, fetched.image, existing)
    const withPhoto = photo ? { ...fields, photo_path: photo, photo_imported: true } : fields
    if (existing) {
      const { error } = await supabase.from('recipes').update(withPhoto).eq('id', existing.id)
      if (error) {
        removePhotos([photo])
        return { ok: false, error: isPlusRequiredError(error) ? 'subscription_required' : 'failed' }
      }
      if (photo && existing.photo_path) removePhotos([existing.photo_path])
      qc.setQueryData<Recipe[]>(key, (old = []) => old.map(r => (r.id === existing.id ? { ...r, ...withPhoto } : r)))
      return { ok: true, id: existing.id, title: fields.title, renamed, ingredients, ...source }
    }
    const now = new Date().toISOString()
    const row: Recipe = {
      id, household_id: householdId, created_by: memberId || null,
      starred: false, tags: [], photo_path: null, photo_imported: false, created_at: now, updated_at: now, ...withPhoto,
    }
    const { error } = await supabase.from('recipes').insert({
      id: row.id, household_id: row.household_id, created_by: row.created_by, ...withPhoto,
    })
    if (error) {
      removePhotos([photo])
      return { ok: false, error: isPlusRequiredError(error) ? 'subscription_required' : 'failed' }
    }
    qc.setQueryData<Recipe[]>(key, (old = []) => [row, ...old.filter(r => r.id !== row.id)])
    return { ok: true, id: row.id, title: fields.title, renamed, ingredients, ...source }
  }

  /**
   * "Refresh from link" on the recipe page: reads the recipe's page again and
   * fills in only what the saved recipe is missing (lib/recipeRefresh.ts), so
   * hand edits survive. Returns which fields were filled.
   */
  async function refreshRecipe(id: string): Promise<RefreshOutcome> {
    const saved = (qc.getQueryData<Recipe[]>(key) ?? []).find(r => r.id === id)
    if (!saved?.url) return { ok: false, error: 'failed' }
    const fetched = await fetchImport({ url: saved.url })
    if (!fetched.ok) {
      if (fetched.error === 'subscription_required') onPlusRequired?.()
      return fetched
    }
    const { patch, filled } = fillMissing(saved, fetched.fields)
    // A recipe saved without a photo (before imports kept one, or when the
    // fetch missed it) gets the page's; one it has is never replaced.
    const photo = saved.photo_path ? null : await keepImportedPhoto(id, fetched.image, saved)
    const added: RefreshAdded[] = photo ? [...filled, 'photo'] : filled
    if (added.length === 0) return { ok: true, filled: added }
    const row = photo ? { ...patch, photo_path: photo, photo_imported: true } : patch
    const { error } = await supabase.from('recipes').update(row).eq('id', id)
    if (error) {
      removePhotos([photo])
      if (isPlusRequiredError(error)) { onPlusRequired?.(); return { ok: false, error: 'subscription_required' } }
      return { ok: false, error: 'failed' }
    }
    qc.setQueryData<Recipe[]>(key, (old = []) => old.map(r => (r.id === id ? { ...r, ...row } : r)))
    return { ok: true, filled: added }
  }

  /**
   * The recipe editor's save: a new recipe written by hand (id null) or every
   * field of an existing one, and its photo. The photo is uploaded first (the
   * storage policies key on the household folder, not on the row), then the row
   * points at it; whatever it pointed at before is removed after.
   */
  async function saveRecipe(id: string | null, fields: RecipeFields, photo: PhotoChange): Promise<SaveOutcome> {
    const existing = id ? (qc.getQueryData<Recipe[]>(key) ?? []).find(r => r.id === id) : undefined
    const recipeId = existing?.id ?? uuid()
    let uploaded: string | null = null
    if (photo.kind === 'set') {
      const path = photoPath(householdId, recipeId)
      const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, photo.blob, { contentType: 'image/jpeg', upsert: false })
      if (error) return { ok: false, error: 'photo_failed' }
      uploaded = path
    }
    const photo_path = photo.kind === 'set' ? uploaded : photo.kind === 'remove' ? null : existing?.photo_path ?? null
    // A member's own photo, or none: only an untouched imported one stays imported.
    const photo_imported = photo.kind === 'keep' ? existing?.photo_imported ?? false : false
    const row = { ...fields, photo_path, photo_imported }

    const { error } = existing
      ? await supabase.from('recipes').update(row).eq('id', recipeId)
      : await supabase.from('recipes').insert({ id: recipeId, household_id: householdId, created_by: memberId || null, ...row })
    if (error) {
      removePhotos([uploaded])
      if (isPlusRequiredError(error)) { onPlusRequired?.(); return { ok: false, error: 'subscription_required' } }
      return { ok: false, error: 'failed' }
    }
    if (existing?.photo_path && existing.photo_path !== photo_path) removePhotos([existing.photo_path])

    const now = new Date().toISOString()
    qc.setQueryData<Recipe[]>(key, (old = []) => existing
      ? old.map(r => (r.id === recipeId ? { ...r, ...row, updated_at: now } : r))
      : [{
          id: recipeId, household_id: householdId, url: null, created_by: memberId || null,
          starred: false, tags: [], vegetarian: null, source: null, rating_value: null, rating_count: null,
          created_at: now, updated_at: now, ...row,
        }, ...old.filter(r => r.id !== recipeId)])
    return { ok: true, id: recipeId }
  }

  const updateRecipe = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: RecipePatch }) => {
      const { error } = await supabase.from('recipes').update(patch).eq('id', id)
      if (error) throw error
    },
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: key })
      const snap = snapshotRow(qc.getQueryData<Recipe[]>(key) ?? [], id)
      qc.setQueryData<Recipe[]>(key, old => old?.map(r => (r.id === id ? { ...r, ...patch } : r)))
      return { snap }
    },
    // Takes back this recipe only; a partner's edits meanwhile stay (lib/rows.ts).
    onError: (err, { id }, ctx) => {
      if (ctx?.snap) qc.setQueryData<Recipe[]>(key, old => revertRow(old ?? [], id, ctx.snap))
      if (isPlusRequiredError(err)) onPlusRequired?.()
    },
  })


  const deleteRecipe = useMutation({
    mutationFn: async ({ id, photo }: { id: string; photo: string | null }) => {
      const { error } = await supabase.from('recipes').delete().eq('id', id)
      if (error) throw error
      removePhotos([photo])
    },
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: key })
      const snap = snapshotRow(qc.getQueryData<Recipe[]>(key) ?? [], id)
      qc.setQueryData<Recipe[]>(key, old => old?.filter(r => r.id !== id))
      return { snap }
    },
    onError: (err, { id }, ctx) => {
      if (ctx?.snap) qc.setQueryData<Recipe[]>(key, old => revertRow(old ?? [], id, ctx.snap))
      if (isPlusRequiredError(err)) onPlusRequired?.()
    },
  })

  return {
    recipes: recipesQuery.data ?? [],
    isLoading: recipesQuery.isLoading,
    importRecipe,
    importText,
    updateRecipe: (id: string, patch: RecipePatch) => updateRecipe.mutate({ id, patch }),
    saveRecipe,
    refreshRecipe,
    deleteRecipe: (id: string) => deleteRecipe.mutate({
      id, photo: (qc.getQueryData<Recipe[]>(key) ?? []).find(r => r.id === id)?.photo_path ?? null,
    }),
  }
}

// Signed URLs last an hour; refetched a little before, and only for the paths
// that changed (the key is the set of paths).
const PHOTO_URL_TTL_S = 3600

/** recipe id → a signed URL of its photo, for the covers. Private bucket (035), so never a public URL. */
export function useRecipePhotos(recipes: Pick<Recipe, 'id' | 'photo_path'>[]): Map<string, string> {
  const paths = useMemo(() => [...new Set(recipes.map(r => r.photo_path).filter((p): p is string => !!p))].sort(), [recipes])
  const { data } = useQuery({
    queryKey: ['recipe-photos', paths],
    enabled: paths.length > 0,
    staleTime: (PHOTO_URL_TTL_S - 600) * 1000,
    refetchInterval: (PHOTO_URL_TTL_S - 600) * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(PHOTO_BUCKET).createSignedUrls(paths, PHOTO_URL_TTL_S)
      if (error) throw error
      return new Map((data ?? []).filter(d => d.signedUrl && !d.error).map(d => [d.path as string, d.signedUrl]))
    },
  })
  return useMemo(() => {
    const out = new Map<string, string>()
    for (const r of recipes) {
      const url = r.photo_path ? data?.get(r.photo_path) : undefined
      if (url) out.set(r.id, url)
    }
    return out
  }, [recipes, data])
}
